import { Routes } from '@common/constants';
import { useTranslations } from '@lang';
import { entitySelectors } from '@store/entity/selectors';
import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import { Category } from 'shared-types/Common';
import { CollectionItem, FeaturedLinkItem, HomeTemplate } from 'voyager';
import { HomeV2Props } from './HomeV2.types';
import { useHomeCategories } from './useHomeCategories';

type HomepageFeaturedItem = {
  title: string;
  subtitle?: string;
  url?: string;
  slug?: string;
  htmlTarget?: string;
  image?: { url?: string };
};

type HomepageCategory = Category & { isPrivate?: boolean };

type HomepageEntity = {
  featuredItem?: HomepageFeaturedItem;
  categories?: HomepageCategory[];
  heroBackgroundImage?: { url?: string };
};

export const HomeV2: FC<HomeV2Props> = ({ property, language }) => {
  const router = useRouter();
  const { t } = useTranslations();

  const homepageId = property.homepage?.id;
  const homepage = useSelector((state) =>
    homepageId ? (entitySelectors.getHomepage(state, homepageId) as HomepageEntity) : null
  );

  const heroImageUrl =
    homepage?.heroBackgroundImage?.url || property.homepage?.heroBackgroundImage?.url || null;
  const propertyLogoUrl = property.logo?.url || null;

  const { categories, selectCategory } = useHomeCategories({
    property,
    language,
    retailListLoaded: true,
  });

  const featuredLink = useMemo<FeaturedLinkItem | null>(() => {
    const item = homepage?.featuredItem;
    if (!item) return null;

    const ctaLabel = item.slug === 'tables' ? t('home.bookTable') : t('home.viewDetails');

    const onCtaClick = () => {
      if (item.slug === 'tables') {
        router.push({ pathname: Routes.TABLES_HOME });
        return;
      }
      if (!item.url) return;
      if (!/^(https?:\/\/|\/)/.test(item.url)) return;
      const target = item.htmlTarget === '_blank' ? '_blank' : '_self';
      window.open(item.url, target, 'noopener,noreferrer');
    };

    return {
      title: item.title,
      subtitle: item.subtitle,
      imageUrl: item.image?.url || null,
      ctaLabel,
      onCtaClick,
    };
  }, [homepage?.featuredItem, router, t]);

  const collections = useMemo<CollectionItem[]>(() => {
    const list = homepage?.categories;
    if (!list?.length) return [];
    return list
      .filter((category) => !category.isPrivate)
      .map((category) => ({
        id: String(category.id),
        label: category.name,
        imageUrl: category.image?.url || null,
      }));
  }, [homepage?.categories]);

  const handleSelectCollection = useCallback(
    (id: string) => {
      router.push({ pathname: Routes.CATEGORY_DETAIL, query: { id } });
    },
    [router]
  );

  return (
    <HomeTemplate
      propertyName={property.name}
      propertyLogoUrl={propertyLogoUrl}
      heroImageUrl={heroImageUrl}
      heading={t('journeypicker.cta')}
      categories={categories}
      onSelectCategory={selectCategory}
      featuredLink={featuredLink}
      collections={collections}
      onSelectCollection={handleSelectCollection}
      collectionsHeading={t('homepage.featuredCollections')}
    />
  );
};
